import React, { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { GoogleOAuthProvider } from "@react-oauth/google";

// Pages
import Landing from "./pages/Landing";
import About from "./pages/About";
import { devError } from "./utils/devLogger";
import Dashboard from "./pages/Dashboard";
import Questionnaire from "./pages/Questionnaire";
import TrainingPlan from "./pages/TrainingPlan";
import Progress from "./pages/Progress";
import Achievements from "./pages/Achievements";
import WeeklyCalendar from "./pages/WeeklyCalendar";
import CognitiveGames from "./pages/CognitiveGames";
import Mindfulness from "./pages/Mindfulness";
import WeeklyReport from "./pages/WeeklyReport";
import Challenges from "./pages/Challenges";
import Settings from "./pages/Settings";
import Auth from "./pages/Auth";
import ProfileCard from "./pages/ProfileCard";
import WeeklyDigest from "./pages/WeeklyDigest";
import ScoreDetail from "./pages/ScoreDetail";
import NotFound from "./pages/NotFound";
import DeepDive from "./pages/DeepDive";
import Subscription from "./pages/Subscription";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import WorkoutLab from "./pages/WorkoutLab";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Messages from "./pages/Messages";
import Blog from "./pages/Blog";
import HealthAssessment from "./pages/HealthAssessment";
import AdminPanel from "./pages/AdminPanel";
import Referral from "./pages/Referral";
import SocialFeed from "./pages/SocialFeed";
import SeasonalChallenges from "./pages/SeasonalChallenges";
import Contact from "./pages/Contact";
import FeatureMenuPage from "./components/FeatureMenu";
import Onboarding from "./pages/Onboarding";
import ErrorBoundary from "./components/ErrorBoundary";
import HelpAgent from "./components/HelpAgent";
import BrowserCompatBanner from "./components/BrowserCompatBanner";
import { SubscriptionProvider } from "./components/SubscriptionGate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Token storage helpers — uses sessionStorage (cleared on tab close, not accessible via XSS on other origins)
const TOKEN_KEY = "lumifit_session_token";
let _memToken = null; // In-memory fallback
const getStoredToken = () => _memToken || sessionStorage.getItem(TOKEN_KEY);
const storeToken = (token) => { if (token) { _memToken = token; sessionStorage.setItem(TOKEN_KEY, token); } };
const clearToken = () => { _memToken = null; sessionStorage.removeItem(TOKEN_KEY); localStorage.removeItem(TOKEN_KEY); };

// Configure axios - use Authorization header instead of cookies
axios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
export const AuthContext = React.createContext(null);

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        toast.error("Authentication failed");
        navigate("/");
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await axios.post(`${API}/auth/session`, {
          session_id: sessionId
        });

        if (response.data.user) {
          storeToken(response.data.session_token);
          // Meta Pixel: track Google OAuth signup/login
          if (window.fbq) window.fbq('track', 'CompleteRegistration', { content_name: 'google_oauth' });
          // Clear hash and navigate to dashboard with user data
          window.history.replaceState(null, "", "/dashboard");
          navigate("/dashboard", { state: { user: response.data.user } });
        } else {
          throw new Error("No user data");
        }
      } catch (error) {
        devError("Auth error:", error);
        toast.error("Authentication failed");
        navigate("/");
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-sky-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-sky-400/30 glow-pulse" />
        <p className="text-sky-700">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user data passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-sky-400/30 glow-pulse" />
          <p className="text-sky-700">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Meta Pixel: track PageView on every route change
  useEffect(() => {
    if (window.fbq) {
      window.fbq("track", "PageView");
    }
  }, [location.pathname]);

  // CRITICAL: Check for session_id synchronously before rendering routes
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<About />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/questionnaire" element={
        <ProtectedRoute>
          <Questionnaire />
        </ProtectedRoute>
      } />
      <Route path="/training" element={
        <ProtectedRoute>
          <TrainingPlan />
        </ProtectedRoute>
      } />
      <Route path="/progress" element={
        <ProtectedRoute>
          <Progress />
        </ProtectedRoute>
      } />
      <Route path="/achievements" element={
        <ProtectedRoute>
          <Achievements />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <WeeklyCalendar />
        </ProtectedRoute>
      } />
      <Route path="/games" element={
        <ProtectedRoute>
          <CognitiveGames />
        </ProtectedRoute>
      } />
      <Route path="/mindfulness" element={
        <ProtectedRoute>
          <Mindfulness />
        </ProtectedRoute>
      } />
      <Route path="/report" element={
        <ProtectedRoute>
          <WeeklyReport />
        </ProtectedRoute>
      } />
      <Route path="/challenges" element={
        <ProtectedRoute>
          <Challenges />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/profile-card" element={
        <ProtectedRoute>
          <ProfileCard />
        </ProtectedRoute>
      } />
      <Route path="/digest" element={
        <ProtectedRoute>
          <WeeklyDigest />
        </ProtectedRoute>
      } />
      <Route path="/score" element={
        <ProtectedRoute>
          <ScoreDetail />
        </ProtectedRoute>
      } />
      <Route path="/deep-dive" element={
        <ProtectedRoute>
          <DeepDive />
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute>
          <Subscription />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <SubscriptionSuccess />
        </ProtectedRoute>
      } />
      <Route path="/workout-lab" element={
        <ProtectedRoute>
          <WorkoutLab />
        </ProtectedRoute>
      } />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/health-assessment" element={
        <ProtectedRoute>
          <HealthAssessment />
        </ProtectedRoute>
      } />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <Onboarding />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={<AdminPanel />} />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Messages />
        </ProtectedRoute>
      } />
      <Route path="/referral" element={
        <ProtectedRoute>
          <Referral />
        </ProtectedRoute>
      } />
      <Route path="/social-feed" element={
        <ProtectedRoute>
          <SocialFeed />
        </ProtectedRoute>
      } />
      <Route path="/seasonal-challenges" element={
        <ProtectedRoute>
          <SeasonalChallenges />
        </ProtectedRoute>
      } />
      <Route path="/features" element={
        <ProtectedRoute>
          <FeatureMenuPage />
        </ProtectedRoute>
      } />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// Auth Provider
function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  
  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    // AuthCallback will exchange the session_id and establish the session first
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return children;
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <ErrorBoundary>
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <BrowserRouter>
            <AuthProvider>
              <SubscriptionProvider>
                <AppRouter />
                <HelpAgent />
                <BrowserCompatBanner />
              </SubscriptionProvider>
            </AuthProvider>
          </BrowserRouter>
        </GoogleOAuthProvider>
      </ErrorBoundary>
    </div>
  );
}



export { API, BACKEND_URL, GOOGLE_CLIENT_ID, storeToken, clearToken, getStoredToken };
export default App;
